import React, { useEffect, useRef } from 'react';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { Button } from './Button';

export interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

/**
 * Reusable confirmation dialog for delete/destructive actions
 * Features: Modal overlay, Escape key handling, focus trap, loading state
 */
export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
}: ConfirmationDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  // Focus trap - focus cancel button when dialog opens
  useEffect(() => {
    if (isOpen && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // Icon configuration based on variant
  const iconConfig = {
    danger: {
      Icon: AlertTriangle,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
    },
    warning: {
      Icon: AlertCircle,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
    info: {
      Icon: Info,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
  };

  const { Icon, color, bgColor } = iconConfig[variant];

  // Button variant mapping
  const buttonVariant = variant === 'danger' ? 'danger' : 'primary';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-message"
    >
      <div
        ref={dialogRef}
        className="bg-dark-surface rounded-xl shadow-2xl shadow-black/20 max-w-md w-full border border-dark-border/30 overflow-hidden"
      >
        {/* Header with Icon */}
        <div className="px-6 py-4">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className={`flex-shrink-0 ${bgColor} rounded-full p-2`}>
              <Icon className={`w-6 h-6 ${color}`} />
            </div>

            {/* Title and Message */}
            <div className="flex-1 min-w-0">
              <h2
                id="confirmation-dialog-title"
                className="text-lg font-medium text-white mb-2"
              >
                {title}
              </h2>
              <p
                id="confirmation-dialog-message"
                className="text-sm text-dark-text"
              >
                {message}
              </p>
            </div>
          </div>
        </div>

        {/* Footer with Buttons */}
        <div className="px-6 py-4 bg-dark-bg border-t border-dark-border/30 flex gap-3 justify-end">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium bg-dark-surface/50 hover:bg-dark-surface/50 active:bg-dark-input text-white rounded transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-dark-bg focus:ring-brand-400 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] md:min-h-0"
            data-testid="confirmation-dialog-cancel"
            data-ux="cancel"
            data-interaction-id='confirmation-dialog-cancel'>
            {cancelText}
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium rounded transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-dark-bg disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] md:min-h-0 ${
              variant === 'danger'
                ? 'bg-red-600 hover:bg-red-700 active:bg-red-800 text-white focus:ring-red-500'
                : 'bg-brand-600 hover:bg-brand-500 active:bg-brand-700 text-white focus:ring-brand-400'
            }`}
            data-testid="confirmation-dialog-confirm"
            data-ux="confirm"
            data-interaction-id='confirmation-dialog-confirm'>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {confirmText}
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
